import { makeAutoObservable, runInAction } from 'mobx';
import { registerSW } from 'virtual:pwa-register';
import type RootStore from './RootStore';
import type NotificationStore from './NotificationStore';

export class PWAStore {
    registration: ServiceWorkerRegistration | null = null;
    updateAvailable = false;
    offlineReady = false;
    needRefresh = false;
    installingUpdate = false;

    // Install prompt
    deferredPrompt: any = null;
    isInstallable = false;
    isInstalled = false;
    isStandalone = false;

    // Sync status
    hasPendingSync = false;
    syncInProgress = false;
    lastSyncTime: Date | null = null;

    private updateSW: ((reloadPage?: boolean) => Promise<void>) | null = null;

    constructor(private rootStore: RootStore) {
        makeAutoObservable(this);
        this.initializePWA();
        this.setupInstallPrompt();
    }

    private initializePWA() {
        this.updateSW = registerSW({
            immediate: true,

            onRegisteredSW: (swUrl, registration) => {
                console.log(`Service Worker registered: ${swUrl}`);
                runInAction(() => {
                    this.registration = registration || null;
                });

                // Check for updates every hour
                setInterval(() => {
                    registration?.update();
                }, 60 * 60 * 1000);
            },

            onRegisterError: (error: any) => {
                console.error('Service Worker registration error:', error);
            },

            onNeedRefresh: () => {
                runInAction(() => {
                    this.needRefresh = true;
                    this.updateAvailable = true;
                });

                if (this.rootStore.notificationStore) {
                    this.rootStore.notificationStore.addNotification({
                        message: 'New version available! Click to update.',
                        options: {
                            variant: 'info',
                            autoHideduration: 0, // Persistent
                            action: {
                                label: 'Update',
                                callback: () => this.updateApp()
                            }
                        }
                    });
                }
            },

            onOfflineReady: () => {
                runInAction(() => {
                    this.offlineReady = true;
                });

                if (this.rootStore.notificationStore) {
                    this.rootStore.notificationStore.addNotification({
                        message: 'App ready to work offline!',
                        options: {
                            variant: 'success',
                            autoHideduration: 3000
                        }
                    });
                }
            }
        });

        // Listen for messages from service worker
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', this.handleSWMessage);
        }

        // Check if already installed
        this.checkInstallState();
    }

    private handleSWMessage = (event: MessageEvent) => {
        const { type, ...data } = event.data;

        switch (type) {
            case 'SYNC_COMPLETE':
                runInAction(() => {
                    this.hasPendingSync = false;
                    this.lastSyncTime = new Date();
                });

                // Trigger offline queue processing if needed
                if (this.rootStore.offlineQueueManager) {
                    this.rootStore.offlineQueueManager.processQueue(async () => false);
                }
                break;

            case 'API_QUEUED':
                runInAction(() => {
                    this.hasPendingSync = true;
                });
                break;

            case 'API_SUCCESS':
                // Handle successful API call
                break;

            case 'CRITICAL_DATA_UPDATED':
                // Could trigger store refresh here if needed
                break;

            case 'SYNC_PROGRESS':
                runInAction(() => {
                    this.syncInProgress = data.inProgress;
                });
                break;
        }
    };

    async updateApp() {
        if (!this.updateSW) return;

        runInAction(() => {
            this.installingUpdate = true;
        });

        try {
            await this.updateSW(true); // true = reload page
        } catch (error) {
            console.error('Failed to update app:', error);
            runInAction(() => {
                this.installingUpdate = false;
            });
        }
    }

    async checkForUpdates() {
        if (!this.registration) return;

        try {
            await this.registration.update();

            if (this.registration.waiting) {
                runInAction(() => {
                    this.updateAvailable = true;
                });
            }
        } catch (error) {
            console.error('Failed to check for updates:', error);
        }
    }

    private setupInstallPrompt() {
        // Listen for install prompt
        window.addEventListener('beforeinstallprompt', (e: any) => {
            e.preventDefault();
            runInAction(() => {
                this.deferredPrompt = e;
                this.isInstallable = true;
            });
        });

        // Listen for successful install
        window.addEventListener('appinstalled', () => {
            runInAction(() => {
                this.isInstalled = true;
                this.isInstallable = false;
                this.deferredPrompt = null;
            });

            // Track installation (analytics can be added later)
            console.log('PWA installed successfully');
        });
    }

    private checkInstallState() {
        const isStandalone =
            window.matchMedia('(display-mode: standalone)').matches ||
            (window.navigator as any).standalone === true ||
            document.referrer.includes('android-app://');

        runInAction(() => {
            this.isStandalone = isStandalone;
            this.isInstalled = isStandalone;
        });
    }

    async installApp() {
        if (!this.deferredPrompt) {
            throw new Error('Installation not available');
        }

        this.deferredPrompt.prompt();
        const { outcome } = await this.deferredPrompt.userChoice;

        runInAction(() => {
            if (outcome === 'accepted') {
                this.isInstalled = true;
            }
            this.deferredPrompt = null;
            this.isInstallable = false;
        });

        return outcome === 'accepted';
    }

    async triggerSync() {
        if (!('serviceWorker' in navigator)) return;

        const registration = await navigator.serviceWorker.ready;

        // Try background sync first
        if ('sync' in registration) {
            await registration.sync.register('sync-data');
        } else {
            // Fallback to posting message
            navigator.serviceWorker.controller?.postMessage({
                type: 'SYNC_NOW'
            });
        }
    }

    async setupPeriodicSync(tag: string, minInterval: number) {
        if (!('periodicSync' in ServiceWorkerRegistration.prototype)) {
            console.warn('Periodic sync not supported');
            return;
        }

        const registration = await navigator.serviceWorker.ready;
        const status = await navigator.permissions.query({
            name: 'periodic-background-sync' as PermissionName,
        });

        if (status.state === 'granted') {
            await (registration as any).periodicSync.register(tag, {
                minInterval: minInterval
            });
        }
    }

    // Share API integration
    async share(data: ShareData) {
        if (!navigator.share) {
            // Fallback to copying to clipboard
            const text = `${data.title}\n${data.text}\n${data.url}`;
            await navigator.clipboard.writeText(text);
            if (this.rootStore.notificationStore) {
                this.rootStore.notificationStore.addNotification({
                    message: 'Link copied to clipboard',
                    options: {
                        variant: 'success',
                        autoHideduration: 2000
                    }
                });
            }
            return;
        }

        try {
            await navigator.share(data);
        } catch (error: any) {
            if (error.name !== 'AbortError') {
                throw error;
            }
        }
    }
}